import React, { useState } from 'react';
import Modal from '../Modal';
import { Button } from '@mes-amis/galette-design-system';
import { Contact } from '@/db/types/contact';

interface RelationshipOption {
  label: string;
  value: string;
}

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Partial<Contact>) => Promise<void>;
  clientId: number;
  relationshipOptions?: RelationshipOption[];
  /** When provided, the modal pre-fills fields for editing an existing contact */
  initialContact?: Contact;
}

interface FormData {
  // Person fields
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;

  // Address fields
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;

  // Contact fields
  relationship: string;
  note: string;

  // Designations
  designated: boolean;
  emergency: boolean;
  legal_representative: boolean;
  physician: boolean;
  caregiver: boolean;
}

const emptyFormData: FormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  relationship: '',
  note: '',
  designated: false,
  emergency: false,
  legal_representative: false,
  physician: false,
  caregiver: false,
};

function contactToFormData(contact: Contact): FormData {
  const person = contact.person as Record<string, unknown> | undefined;
  const address = contact.address as Record<string, unknown> | undefined;
  const phoneNumbers = person?.phone_numbers as Array<{ number?: string }> | undefined;

  return {
    first_name: (person?.first_name as string) || '',
    last_name: (person?.last_name as string) || '',
    email: (person?.email as string) || '',
    phone_number: phoneNumbers?.[0]?.number || '',
    address_line1: (address?.address_line1 as string) || '',
    address_line2: (address?.address_line2 as string) || '',
    city: (address?.city as string) || '',
    state: (address?.state as string) || '',
    zip: (address?.zip as string) || '',
    relationship: contact.relationship || '',
    note: contact.note || '',
    designated: contact.designated ?? false,
    emergency: contact.emergency ?? false,
    legal_representative: contact.legal_representative ?? false,
    physician: contact.physician ?? false,
    caregiver: contact.caregiver ?? false,
  };
}

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  isOpen,
  onClose,
  onSave,
  clientId,
  relationshipOptions = [],
  initialContact,
}) => {
  const isEditing = !!initialContact;

  const [formData, setFormData] = useState<FormData>(
    initialContact ? contactToFormData(initialContact) : emptyFormData
  );

  // Reset form data when initialContact changes (switching between create/edit)
  React.useEffect(() => {
    if (isOpen) {
      setFormData(initialContact ? contactToFormData(initialContact) : emptyFormData);
      setErrors({});
    }
  }, [isOpen, initialContact]);

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const contactData: Partial<Contact> = {
        customer_id: clientId,
        relationship: formData.relationship || null,
        note: formData.note || null,
        designated: formData.designated,
        emergency: formData.emergency,
        legal_representative: formData.legal_representative,
        physician: formData.physician,
        caregiver: formData.caregiver,
        person: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email || null,
          phone_numbers: formData.phone_number ? [{ number: formData.phone_number }] : []
        } as any,
        address: {
          address_line1: formData.address_line1,
          address_line2: formData.address_line2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zip: formData.zip
        } as any
      };

      await onSave(contactData);
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditing ? 'Edit Contact' : 'Add Contact'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Person Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Contact Information</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className={`block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 ${
                  errors.first_name
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300'
                } focus:outline-none focus:ring-0`}
              />
              {errors.first_name && <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className={`block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 ${
                  errors.last_name
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300'
                } focus:outline-none focus:ring-0`}
              />
              {errors.last_name && <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 ${
                errors.email
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[var(--color-indigo-60)]'
              } focus:outline-none focus:ring-0`}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              id="phone_number"
              value={formData.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              placeholder="(555) 123-4567"
              className="block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300 focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        {/* Address */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Address</h3>

          <div>
            <label htmlFor="address_line1" className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input
              type="text"
              id="address_line1"
              value={formData.address_line1}
              onChange={(e) => handleInputChange('address_line1', e.target.value)}
              className={`block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 ${
                errors.address_line1
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[var(--color-indigo-60)]'
              } focus:outline-none focus:ring-0`}
            />
            {errors.address_line1 && <p className="mt-1 text-xs text-red-600">{errors.address_line1}</p>}
          </div>

          <div>
            <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700 mb-1">
              Apartment, suite, etc.
            </label>
            <input
              type="text"
              id="address_line2"
              value={formData.address_line2}
              onChange={(e) => handleInputChange('address_line2', e.target.value)}
              className="block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300 focus:outline-none focus:ring-0"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300 focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                maxLength={2}
                placeholder="CA"
                className="block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300 focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                type="text"
                id="zip"
                value={formData.zip}
                onChange={(e) => handleInputChange('zip', e.target.value)}
                className={`block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 ${
                  errors.zip
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300'
                } focus:outline-none focus:ring-0`}
              />
              {errors.zip && <p className="mt-1 text-xs text-red-600">{errors.zip}</p>}
            </div>
          </div>
        </div>

        {/* Relationship */}
        <div>
          <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">
            Relationship to Client
          </label>
          <select
            id="relationship"
            value={formData.relationship}
            onChange={(e) => handleInputChange('relationship', e.target.value)}
            className="block w-full rounded-lg border-2 px-4 py-3 pr-10 text-sm bg-white text-gray-900 transition-colors duration-200 appearance-none border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300 focus:outline-none focus:ring-0"
          >
            <option value="">Select Relationship</option>
            {relationshipOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Note */}
        <div>
          <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
            Note
          </label>
          <textarea
            id="note"
            value={formData.note}
            onChange={(e) => handleInputChange('note', e.target.value)}
            rows={3}
            className="block w-full rounded-lg border-2 px-4 py-3 text-sm bg-white text-gray-900 transition-colors duration-200 border-gray-200 focus:border-[var(--color-indigo-60)] hover:border-gray-300 focus:outline-none focus:ring-0"
          />
        </div>

        {/* Designations */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Designations</h3>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.designated}
              onChange={(e) => handleInputChange('designated', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Designate as <strong>Proxy</strong></span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.emergency}
              onChange={(e) => handleInputChange('emergency', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Designate as <strong>Emergency Contact</strong></span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.legal_representative}
              onChange={(e) => handleInputChange('legal_representative', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Designate as <strong>Legal Representative</strong></span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.physician}
              onChange={(e) => handleInputChange('physician', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Designate as <strong>Physician</strong></span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.caregiver}
              onChange={(e) => handleInputChange('caregiver', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">Designate as <strong>Caregiver</strong></span>
          </label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Contact'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateContactModal;
